import React, { useState } from "react";
import { DateTime } from "luxon";
import { TListingValuationTabOutput } from "../../../../domain/codecs/formIO/ListingValuationTabFormIO";
import { penceToCopyText } from "../../../../shared/src/util";
import { CRMCardOutsideLabelled } from "../CRMCardOutsideLabelled/CRMCardOutsideLabelled";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { Padding } from "../BuildingBlocks/Padding";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { CRMInputCurrency } from "../CRMInputCurrency/CRMInputCurrency";
import CRMTextAreaComponent from "../CRMTextAreaComponent/CRMTextAreaComponent";
import { CRMInputLabelAndErrorWrapComponent } from "../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { CRMButtonSecondary } from "../CRMButtonSecondary/CRMButtonSecondary";
import { CRMButtonTertiary } from "../CRMButtonTertiary/CRMButtonTertiary";
import { TOnValuationAction } from "./valuationTabSteps";

const INELIGIBLE_LABELS: Record<string, string> = {
    INSUFFICIENT_DATA: "We couldn't find enough about this property to price it"
        + " automatically — no sold comparables and no data from our suppliers.",
    NO_COMPARABLES: "We couldn't find any comparable sales near this property.",
    UNSUPPORTED_PROPERTY_TYPE: "This kind of property isn't one we price automatically.",
};

const ineligibleText = (reason: string | null): string =>
    reason === null
        ? "Nothing has been worked out for this property yet."
        : INELIGIBLE_LABELS[reason]
            ?? `We couldn't price this automatically (${reason.toLowerCase().replace(/_/g, " ")}).`;

const formatDateTime = (isoDateTime: string): string =>
    DateTime.fromISO(isoDateTime).setZone("Europe/London").toFormat("d MMM 'at' HH:mm");

const OtherPrice = (props: { label: string; pence: number }): JSX.Element => (
    <SpacingRow spacing={CRMSpacing.SMALL} childSize="1fr auto" alignItems="center">
        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>{props.label}</Text>
        <Text size={CRMFontSizes.SMALL} align="right">{penceToCopyText(props.pence)}</Text>
    </SpacingRow>
);

// The valuer's own price, over the engine's.
//
// Closed until asked for, because the common case is agreeing with the number and the
// open form invites second-guessing it. The reason is mandatory and enforced on the
// server too: an override with no reason is what makes a version history useless six
// months later, when the only question anyone asks is why this is £15k above what the
// comparables said.
const AmendPrice = (props: {
    currentPence: number | null;
    isSubmitting: boolean;
    onAction: TOnValuationAction;
}): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const [pricePence, setPricePence] = useState<number | null>(props.currentPence);
    const [reason, setReason] = useState("");

    const canSave = pricePence !== null && pricePence > 0 && reason.trim() !== "";

    if (!isOpen) {
        return (
            <CRMButtonTertiary
                label={props.currentPence === null ? "Set the price yourself" : "Use a different price"}
                fullWidth={false}
                disabled={props.isSubmitting}
                onClick={() => setIsOpen(true)}
            />
        );
    }

    return (
        <SpacingColumn spacing={CRMSpacing.SMALL}>
            <CRMInputLabelAndErrorWrapComponent label="Your price">
                <CRMInputCurrency
                    value={pricePence}
                    mode="pence"
                    disabled={props.isSubmitting}
                    displayError={false}
                    onChange={setPricePence}
                />
            </CRMInputLabelAndErrorWrapComponent>
            <CRMInputLabelAndErrorWrapComponent
                label={props.currentPence === null
                    ? "How you arrived at it"
                    : "Why this rather than the worked-out price"
                }
            >
                <CRMTextAreaComponent
                    value={reason}
                    placeholder="Extended to the side, and the two nearest comparables both predate it."
                    disabled={props.isSubmitting}
                    displayError={false}
                    onChange={setReason}
                />
            </CRMInputLabelAndErrorWrapComponent>
            <SpacingRow spacing={CRMSpacing.SMALL} childSize="auto auto" alignItems="center">
                <CRMButtonSecondary
                    label="Save this price"
                    fullWidth={false}
                    disabled={props.isSubmitting || !canSave}
                    onClick={() => props.onAction("amend_price", {
                        amend_price_pence: pricePence,
                        amend_reason: reason.trim(),
                    })}
                />
                <CRMButtonTertiary
                    label="Cancel"
                    fullWidth={false}
                    disabled={props.isSubmitting}
                    onClick={() => {
                        setIsOpen(false);
                        setPricePence(props.currentPence);
                        setReason("");
                    }}
                />
            </SpacingRow>
        </SpacingColumn>
    );
};

export const CRMValuationAnswerCard = (props: {
    output: TListingValuationTabOutput;
    isSubmitting: boolean;
    onAction: TOnValuationAction;
}): JSX.Element => {
    const { output } = props;

    if (output.market_price_pence === null) {
        return (
            <CRMCardOutsideLabelled labelColor="neutral-8">
                <Padding spacing={CRMSpacing.MEDIUM}>
                    <SpacingColumn spacing={CRMSpacing.TINY}>
                        <Text size={CRMFontSizes.LARGE}><WeightBold>No price yet</WeightBold></Text>
                        <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4}>
                            {ineligibleText(output.ineligible_reason)}
                        </Text>
                        {/* The engine declining is exactly when a valuer has to supply
                            the number themselves, so this is the one place the control
                            must not be hidden. It was, until this — the automatic price
                            was a precondition for overriding the automatic price. */}
                        <AmendPrice
                            key={output.versions[0]?.version_id ?? "no-version"}
                            currentPence={null}
                            isSubmitting={props.isSubmitting}
                            onAction={props.onAction}
                        />
                    </SpacingColumn>
                </Padding>
            </CRMCardOutsideLabelled>
        );
    }

    return (
        <CRMCardOutsideLabelled labelColor="highlights-calming-pink-2">
            <Padding spacing={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <SpacingColumn spacing={CRMSpacing.TINY}>
                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>GUIDE PRICE TO ADVERTISE</Text>
                        <Text size={CRMFontSizes.XX_LARGE}><WeightBold>{penceToCopyText(output.market_price_pence)}</WeightBold></Text>
                        {/* "To achieve £412,000" was the single most misread string in
                            testing — read as net-of-fees, as a negotiating floor, and as
                            a second competing price. It needs a verb. */}
                        {output.to_achieve_pence !== null &&
                            <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>
                                {`Likely to sell for about ${penceToCopyText(output.to_achieve_pence)}`}
                                {output.low_range_pence !== null && output.high_range_pence !== null &&
                                    ` (between ${penceToCopyText(output.low_range_pence)} and ${penceToCopyText(output.high_range_pence)})`
                                }
                            </Text>
                        }
                    </SpacingColumn>

                    {/* The staleness verdict, as a verdict. This was previously a pale pill
                        beside an identically-styled version pill, and six of ten testers
                        read it as version bookkeeping — including the one whose whole job
                        was deciding whether the valuation was safe to send. */}
                    {output.is_stale &&
                        <Background
                            color={CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_12}
                            padding={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}
                            borderRadius="3px"
                        >
                            <SpacingColumn spacing={CRMSpacing.TINY}>
                                <Text size={CRMFontSizes.SMALL}>
                                    <WeightBold>This price may be out of date</WeightBold>
                                </Text>
                                <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_2}>
                                    {`We received newer information about the property after this price was worked out${
                                        output.valued_at === null ? "" : ` on ${formatDateTime(output.valued_at)}`
                                    }. Check it before sending to the vendor.`}
                                </Text>
                            </SpacingColumn>
                        </Background>
                    }

                    {!output.is_stale && output.confidence_band !== null &&
                        <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>
                            {`Confidence: ${output.confidence_band}`}
                            {output.confidence_score === null
                                ? ""
                                : ` (${Math.round(output.confidence_score * 100)}%)`
                            }
                        </Text>
                    }

                    {(output.auction_price_pence !== null
                        || output.refurbished_pence !== null
                        || (output.date_of_death_pence !== null && output.reason_for_sale === "probate")) &&
                        <SpacingColumn spacing={CRMSpacing.TINY}>
                            {output.auction_price_pence !== null &&
                                <OtherPrice label="If sold at auction (reserve)" pence={output.auction_price_pence} />
                            }
                            {output.refurbished_pence !== null &&
                                <OtherPrice label="If refurbished first" pence={output.refurbished_pence} />
                            }
                            {/* Same rule as the client report: the engine can attach a
                                date-of-death figure to any valuation, so the listing's own
                                reason for sale has to agree before it is labelled probate. */}
                            {output.date_of_death_pence !== null && output.reason_for_sale === "probate" &&
                                <OtherPrice label="Probate: value at date of death" pence={output.date_of_death_pence} />
                            }
                        </SpacingColumn>
                    }

                    {/* Keyed on the version so a saved price closes the form and a
                        recompute landing underneath resets it. Both are the same event
                        from the form's point of view — the number it was editing is no
                        longer the number on the card — and leaving it open would show
                        the valuer their new price above a form still offering the old
                        one as if nothing had happened. */}
                    <AmendPrice
                        key={output.versions[0]?.version_id ?? "no-version"}
                        currentPence={output.market_price_pence}
                        isSubmitting={props.isSubmitting}
                        onAction={props.onAction}
                    />
                </SpacingColumn>
            </Padding>
        </CRMCardOutsideLabelled>
    );
};
